<template>
  <div class="goods-detail-page">
    <div class="layout-container">
      <div v-if="goods.id" class="goods-page">
        <div class="goods-top">
          <div class="gallery">
            <div class="main-img">
              <img :src="currentImg" alt="" @error="onImgError"/>
            </div>
            <div v-if="allImages.length > 1" class="thumb-row">
              <button
                  v-for="(img, idx) in allImages"
                  :key="idx"
                  type="button"
                  :class="['thumb', { active: currentImg === img }]"
                  @click="currentImg = img"
              >
                <img :src="img" alt="" @error="onThumbError($event, idx)"/>
              </button>
            </div>
          </div>

          <div class="buy-panel">
            <h1 class="goods-title">{{ goods.title }}</h1>
            <div class="goods-price">¥ {{ displayPrice }}</div>
            <div class="goods-meta">
              <span>销量 {{ goods.salesNum || 0 }}</span>
              <span>库存 {{ goods.stock != null ? goods.stock : '-' }}</span>
            </div>

            <div v-if="specList.length" class="block">
              <div class="block-label">规格</div>
              <div class="spec-row">
                <button
                    v-for="s in specList"
                    :key="s"
                    type="button"
                    :class="['spec-chip', { active: selectedSpec === s }]"
                    @click="selectedSpec = s"
                >{{ s }}</button>
              </div>
            </div>

            <div v-if="paramList.length" class="block params-preview">
              <div class="block-label">商品参数</div>
              <ul class="param-mini">
                <li v-for="(p, i) in paramList.slice(0, 4)" :key="i">
                  <span>{{ p.label }}</span>
                  <span>{{ p.value }}</span>
                </li>
              </ul>
            </div>

            <div class="goods-actions">
              <button v-if="hasBought" class="btn-ghost" disabled>已购买</button>
              <button v-else class="btn-primary" @click="buyNow">立即购买</button>
            </div>
          </div>
        </div>

        <div class="detail-tabs page-panel">
          <div class="tab-head">
            <button
                v-for="tab in detailTabs"
                :key="tab.key"
                type="button"
                :class="['tab-btn', { active: activeTab === tab.key }]"
                @click="activeTab = tab.key"
            >{{ tab.label }}</button>
          </div>

          <div v-show="activeTab === 'detail'" class="tab-body">
            <div v-if="displayContent" class="desc-html" v-html="displayContent"></div>
            <p v-else class="empty-hint">暂无文字介绍</p>
          </div>

          <div v-show="activeTab === 'params'" class="tab-body">
            <table v-if="paramList.length" class="param-table">
              <tbody>
                <tr v-for="(p, i) in paramList" :key="i">
                  <th>{{ p.label }}</th>
                  <td>{{ p.value }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="empty-hint">暂无参数信息</p>
          </div>

          <div v-show="activeTab === 'gallery'" class="tab-body">
            <div v-if="detailImages.length" class="detail-gallery">
              <figure v-for="(img, idx) in detailImages" :key="idx">
                <img :src="img" alt="" loading="lazy" @error="onGalleryError($event, idx)"/>
              </figure>
            </div>
            <p v-else class="empty-hint">暂无图文介绍</p>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">商品不存在或已下架</div>
    </div>

    <div v-if="qrPayVisible" class="qr-pay-mask" @click.self="closeQrPay">
      <div class="qr-pay-dialog">
        <h3>微信扫码支付</h3>
        <p class="qr-tip">请使用微信扫一扫完成支付</p>
        <img v-if="qrPayUrl" :src="qrPayUrl" alt="支付二维码" class="qr-img"/>
        <button type="button" class="btn-ghost" @click="closeQrPay">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getGoodsInfo, isBuyGoods } from '@/utils/http'
import { startWechatPay, buildWechatQrUrl, handleWechatPayError, startWechatPayPoll, stopWechatPayPoll } from '@/utils/wxPay'

const FALLBACK = 'https://picsum.photos/seed/goods-fallback/800/450'

export default {
  name: 'GoodsDetail',
  data() {
    return {
      goods: {},
      hasBought: false,
      currentImg: '',
      selectedSpec: '',
      activeTab: 'detail',
      detailTabs: [
        { key: 'detail', label: '商品详情' },
        { key: 'params', label: '规格参数' },
        { key: 'gallery', label: '图文介绍' }
      ],
      qrPayVisible: false,
      qrPayUrl: ''
    }
  },
  computed: {
    specVariants() {
      return this.parseParamsData().variants
    },
    legacyParamList() {
      return this.parseParamsData().legacy
    },
    specList() {
      if (this.specVariants.length) {
        return this.specVariants.map(v => v.spec).filter(Boolean)
      }
      if (!this.goods.specs) return []
      return this.goods.specs.split(/[,，]/).map(s => s.trim()).filter(Boolean)
    },
    currentVariant() {
      if (!this.specVariants.length) return null
      const name = this.selectedSpec || this.specList[0]
      return this.specVariants.find(v => v.spec === name) || this.specVariants[0]
    },
    displayPrice() {
      const v = this.currentVariant
      if (v && v.price != null && v.price !== '') return v.price
      return this.goods.price
    },
    paramList() {
      if (this.currentVariant && this.currentVariant.params && this.currentVariant.params.length) {
        return this.currentVariant.params
      }
      return this.legacyParamList
    },
    displayContent() {
      const v = this.currentVariant
      if (v && v.content) return v.content
      return this.goods.content || ''
    },
    detailImages() {
      const v = this.currentVariant
      const raw = (v && v.detailImgs) ? v.detailImgs : this.goods.detailImgs
      if (!raw) return []
      return raw.split(/[,，]/).map(s => s.trim()).filter(Boolean)
    },
    allImages() {
      const list = []
      const cover = (this.currentVariant && this.currentVariant.imgUrl) || this.goods.imgUrl
      if (cover) list.push(cover)
      this.detailImages.forEach(url => {
        if (!list.includes(url)) list.push(url)
      })
      return list.length ? list : [FALLBACK]
    }
  },
  watch: {
    selectedSpec() {
      this.$nextTick(() => {
        this.currentImg = this.allImages[0] || FALLBACK
      })
    }
  },
  mounted() {
    this.load()
    if (this.$route.query.payResult === 'success') {
      this.$alert('支付成功', '提示', { confirmButtonText: '确定', center: true })
      this.load()
    }
  },
  beforeDestroy() {
    stopWechatPayPoll()
  },
  methods: {
    closeQrPay() {
      stopWechatPayPoll()
      this.qrPayVisible = false
    },
    parseParamsData() {
      if (!this.goods.goodsParams) return { variants: [], legacy: [] }
      try {
        const parsed = JSON.parse(this.goods.goodsParams)
        if (!Array.isArray(parsed)) return { variants: [], legacy: [] }
        if (parsed.length && parsed[0].spec) {
          return { variants: parsed, legacy: [] }
        }
        return { variants: [], legacy: parsed }
      } catch (e) {
        return { variants: [], legacy: [] }
      }
    },
    onImgError(e) {
      e.target.src = FALLBACK
    },
    onThumbError(e, idx) {
      e.target.src = `https://picsum.photos/seed/goods-thumb-${idx}/120/80`
    },
    onGalleryError(e, idx) {
      e.target.src = `https://picsum.photos/seed/goods-gallery-${idx}/900/600`
    },
    async load() {
      const id = this.$route.query.id
      if (!id) return
      const res = await getGoodsInfo({ id })
      this.goods = res.data || {}
      this.$nextTick(() => {
        this.currentImg = this.allImages[0] || FALLBACK
      })
      if (this.specList.length) {
        this.selectedSpec = this.specList[0]
      } else {
        this.selectedSpec = ''
      }
      if (localStorage.getItem('token')) {
        const buyRes = await isBuyGoods({ goodsId: id })
        this.hasBought = (buyRes.data || 0) > 0
      }
    },
    async buyNow() {
      if (!localStorage.getItem('token')) {
        this.$alert('请先登录', '提示', { confirmButtonText: '确定', center: true })
        this.$router.push('/login')
        return
      }
      try {
        const specTip = this.selectedSpec ? '（' + this.selectedSpec + '）' : ''
        const returnUrl = window.location.origin + window.location.pathname + '?id=' + this.goods.id
        const result = await startWechatPay({
          title: '购买商品【' + this.goods.title + specTip + '】',
          amount: Math.round(Number(this.displayPrice) * 100),
          type: 3,
          id: this.goods.id,
          returnUrl
        })
        if (result.status === 'success') {
          this.$alert('支付成功', '提示', { confirmButtonText: '确定', center: true })
          this.load()
        } else if (result.status === 'native') {
          this.qrPayUrl = buildWechatQrUrl(result.codeUrl)
          this.qrPayVisible = true
          startWechatPayPoll(result.outTradeNo, {
            onPaid: () => {
              this.closeQrPay()
              this.$alert('支付成功', '提示', { confirmButtonText: '确定', center: true })
              this.load()
            }
          }).catch(() => {})
        }
      } catch (e) {
        handleWechatPayError(e)
      }
    }
  }
}
</script>

<style scoped>
.goods-detail-page {
  padding: 24px 0 48px;
}

.goods-top {
  display: grid;
  grid-template-columns: 480px 1fr;
  gap: 32px;
  align-items: start;
  margin-bottom: 28px;
}

.gallery .main-img {
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #111;
  aspect-ratio: 4 / 3;
}

.gallery .main-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb-row {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.thumb {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #111;
}

.thumb.active {
  border-color: var(--tx-accent, #ff6022);
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.buy-panel {
  padding-top: 4px;
}

.goods-title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text);
  line-height: 1.35;
}

.goods-price {
  font-size: 34px;
  font-weight: 700;
  color: var(--tx-accent);
  margin-bottom: 12px;
}

.goods-meta {
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: var(--text-muted);
  margin-bottom: 20px;
}

.block {
  margin-bottom: 18px;
}

.block-label {
  font-size: 14px;
  color: var(--text-muted);
  margin-bottom: 10px;
}

.spec-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.spec-chip {
  padding: 8px 18px;
  border: 1px solid var(--border, #333);
  border-radius: 6px;
  background: var(--surface-2, #1e1e1e);
  color: var(--text);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.spec-chip:hover {
  border-color: var(--tx-accent);
  color: var(--tx-accent);
}

.spec-chip.active {
  border-color: var(--tx-accent);
  background: rgba(255, 96, 34, 0.12);
  color: var(--tx-accent);
}

.param-mini {
  list-style: none;
  padding: 0;
  margin: 0;
}

.param-mini li {
  display: flex;
  gap: 12px;
  font-size: 13px;
  line-height: 2;
  color: var(--text-secondary);
}

.param-mini li span:first-child {
  color: var(--text-muted);
  min-width: 72px;
}

.goods-actions {
  margin-top: 8px;
}

.goods-actions .btn-primary {
  min-width: 180px;
  height: 46px;
  font-size: 16px;
}

.detail-tabs {
  padding: 0;
  overflow: hidden;
}

.tab-head {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-light, #2a2a2a);
  padding: 0 20px;
}

.tab-btn {
  padding: 16px 24px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 15px;
  cursor: pointer;
  position: relative;
  font-family: inherit;
}

.tab-btn.active {
  color: var(--tx-accent);
  font-weight: 600;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 0;
  height: 2px;
  background: var(--tx-accent);
}

.tab-body {
  padding: 24px;
  min-height: 200px;
}

.desc-html {
  font-size: 15px;
  line-height: 1.9;
  color: var(--text-secondary);
}

.desc-html ::v-deep img {
  max-width: 100%;
  border-radius: 8px;
  margin: 12px 0;
}

.param-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.param-table th,
.param-table td {
  padding: 14px 16px;
  border: 1px solid var(--border-light, #2a2a2a);
  text-align: left;
}

.param-table th {
  width: 160px;
  background: var(--surface-2, #1a1a1a);
  color: var(--text-muted);
  font-weight: 500;
}

.param-table td {
  color: var(--text);
}

.detail-gallery {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-gallery figure {
  margin: 0;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: #111;
}

.detail-gallery img {
  width: 100%;
  display: block;
  vertical-align: middle;
}

.empty-hint {
  color: var(--text-muted);
  font-size: 14px;
  text-align: center;
  padding: 40px 0;
}

.qr-pay-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.qr-pay-dialog {
  background: var(--surface, #1a1a1a);
  border-radius: 12px;
  padding: 28px 32px;
  text-align: center;
  min-width: 280px;
}

.qr-pay-dialog h3 {
  margin: 0 0 8px;
  color: var(--text);
}

.qr-tip {
  color: var(--text-muted);
  font-size: 14px;
  margin-bottom: 16px;
}

.qr-img {
  width: 220px;
  height: 220px;
  display: block;
  margin: 0 auto 16px;
  border-radius: 8px;
}

@media (max-width: 900px) {
  .goods-top {
    grid-template-columns: 1fr;
  }
}
</style>
